/**
 * On-device speech in the browser: Moonshine hears and Kokoro-82M speaks, through transformers.js on
 * WebAssembly with 8-bit weights, the same on every browser and phone. No keys and no server: the microphone's
 * audio never leaves the page. The weights download once from Hugging Face (about 60 MB to listen,
 * 90 MB to speak) and stay in the browser's cache. Loaded only when someone turns it on.
 */

export type Progress = (message: string) => void;

/**
 * Where the voice models come from: Hugging Face by default, or a host of your own (set
 * VITE_RALLY_MODEL_HOST at build time, or `__rallyModelHost` before the page loads, as the
 * automated browser check does to use its local copies).
 */
const modelHost = (): string | undefined => (globalThis as { __rallyModelHost?: string }).__rallyModelHost ?? (import.meta.env.VITE_RALLY_MODEL_HOST as string | undefined) ?? undefined;

/** The runtime files we host ourselves, as absolute URLs. */
async function runtimePaths() {
  const [{ default: mjs }, { default: wasm }] = await Promise.all([import('@ort-dist/ort-wasm-simd-threaded.jsep.mjs?url'), import('@ort-dist/ort-wasm-simd-threaded.jsep.wasm?url')]);
  return { mjs: new URL(mjs, location.href).href, wasm: new URL(wasm, location.href).href };
}

// WebGPU would be faster where it exists, but its full-precision weights are several times larger;
// 8-bit weights on WebAssembly keep the one-time download near 150 MB everywhere.
const DEVICE = 'wasm';
const DTYPE = 'q8';

function progressReporter(label: string, onProgress?: Progress) {
  const files = new Map<string, { loaded: number; total: number }>();
  return (p: { status: string; file?: string; loaded?: number; total?: number }) => {
    if (!onProgress || p.status !== 'progress' || !p.file) return;
    files.set(p.file, { loaded: p.loaded ?? 0, total: p.total ?? 0 });
    const loaded = [...files.values()].reduce((a, f) => a + f.loaded, 0);
    const total = [...files.values()].reduce((a, f) => a + f.total, 0);
    if (total) onProgress(`${label}: ${Math.round(loaded / 1e6)} of ${Math.round(total / 1e6)} MB`);
  };
}

type Recognizer = (audio: Float32Array) => Promise<string>;
let recognizer: Promise<Recognizer> | undefined;

/** Moonshine base, ready to transcribe 16 kHz mono audio. */
export function loadListener(onProgress?: Progress): Promise<Recognizer> {
  recognizer ??= (async () => {
    const { pipeline, env } = await import('@huggingface/transformers');
    const onnx = env.backends.onnx as { wasm?: { wasmPaths?: unknown } };
    onnx.wasm = { ...onnx.wasm, wasmPaths: await runtimePaths() };
    const host = modelHost();
    if (host) env.remoteHost = `${host}/`;
    const create = pipeline as unknown as (task: string, model: string, opts: Record<string, unknown>) => Promise<(audio: Float32Array, opts?: Record<string, unknown>) => Promise<{ text: string } | Array<{ text: string }>>>;
    const asr = await create('automatic-speech-recognition', 'onnx-community/moonshine-base-ONNX', { device: DEVICE, dtype: DTYPE, progress_callback: progressReporter('Getting the on-device listener', onProgress) });
    return async (audio: Float32Array) => {
      // transformers.js allows Moonshine floor(seconds) x 6 tokens: nothing for a short "yes". About eight a second.
      const out = await asr(audio, { max_new_tokens: Math.max(12, Math.ceil((audio.length / 16000) * 8)) });
      return (Array.isArray(out) ? out[0]?.text : out.text)?.trim() ?? '';
    };
  })().catch((err) => {
    recognizer = undefined;
    throw err;
  });
  return recognizer;
}

/**
 * Record one utterance: starts on the first sound, stops after a short silence (or `stop()`),
 * and returns 16 kHz mono samples. Echo cancellation keeps Alexa's own voice out.
 */
export function recordUtterance(opts: { maxMs?: number; silenceMs?: number; onLevel?: (level: number) => void } = {}): { done: Promise<Float32Array>; stop: () => void } {
  let stop = () => {};
  const done = (async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    const ctx = new AudioContext();
    const source = ctx.createMediaStreamSource(stream);
    const node = ctx.createScriptProcessor(4096, 1, 1);
    const chunks: Float32Array[] = [];
    let heard = false;
    let quietFor = 0;
    const started = performance.now();
    await new Promise<void>((resolve) => {
      stop = resolve;
      node.onaudioprocess = (e) => {
        const input = e.inputBuffer.getChannelData(0);
        chunks.push(new Float32Array(input));
        const rms = Math.sqrt(input.reduce((a, x) => a + x * x, 0) / input.length);
        opts.onLevel?.(rms);
        const ms = (input.length / ctx.sampleRate) * 1000;
        if (rms > 0.012) {
          heard = true;
          quietFor = 0;
        } else if (heard) quietFor += ms;
        if ((heard && quietFor >= (opts.silenceMs ?? 900)) || performance.now() - started > (opts.maxMs ?? 9000)) resolve();
      };
      source.connect(node);
      node.connect(ctx.destination);
    });
    node.disconnect();
    source.disconnect();
    stream.getTracks().forEach((t) => t.stop());
    const rate = ctx.sampleRate;
    await ctx.close();
    const joined = new Float32Array(chunks.reduce((a, c) => a + c.length, 0));
    let at = 0;
    for (const c of chunks) {
      joined.set(c, at);
      at += c.length;
    }
    // Linear resampling to the recognizer's 16 kHz.
    const ratio = rate / 16000;
    const out = new Float32Array(Math.floor(joined.length / ratio));
    for (let i = 0; i < out.length; i++) {
      const x = i * ratio;
      const i0 = Math.floor(x);
      out[i] = (joined[i0] ?? 0) * (1 - (x - i0)) + (joined[i0 + 1] ?? joined[i0] ?? 0) * (x - i0);
    }
    // Only the words: the recognizer does better without the silence before and after them.
    const words = trimSilence(out);
    // For the automated browser check (scripts/voice-browser.ts): the last utterance, never sent anywhere.
    (globalThis as { __rallyLastUtterance?: Float32Array }).__rallyLastUtterance = words;
    return words;
  })();
  return { done, stop: () => stop() };
}

/** Cut the silence before and after speech at 16 kHz, keeping 150 ms either side. */
function trimSilence(samples: Float32Array): Float32Array {
  const frame = 320;
  const energies: number[] = [];
  for (let i = 0; i < samples.length; i += frame) {
    let e = 0;
    for (let j = i; j < Math.min(samples.length, i + frame); j++) e += samples[j]! * samples[j]!;
    energies.push(Math.sqrt(e / frame));
  }
  const loudest = Math.max(0, ...energies);
  if (!loudest) return samples;
  const threshold = Math.max(0.005, loudest * 0.05);
  const first = energies.findIndex((e) => e > threshold);
  const last = energies.length - 1 - [...energies].reverse().findIndex((e) => e > threshold);
  return samples.slice(Math.max(0, first * frame - 2400), Math.min(samples.length, (last + 1) * frame + 2400));
}

/** Kokoro runs in a Web Worker (speechWorker.ts) so synthesis never freezes the page. */
let worker: Worker | undefined;
let seq = 0;
const waiting = new Map<number, { resolve: (v: { audio?: Float32Array; rate?: number }) => void; reject: (e: Error) => void; onProgress?: (p: { status: string; file?: string; loaded?: number; total?: number }) => void }>();

function ask(msg: Record<string, unknown>, onProgress?: (p: { status: string; file?: string; loaded?: number; total?: number }) => void): Promise<{ audio?: Float32Array; rate?: number }> {
  if (!worker) {
    worker = new Worker(new URL('./speechWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<{ id: number; type: string; p?: never; audio?: Float32Array; rate?: number; message?: string }>) => {
      const w = waiting.get(e.data.id);
      if (!w) return;
      if (e.data.type === 'progress') return w.onProgress?.(e.data.p!);
      waiting.delete(e.data.id);
      if (e.data.type === 'error') w.reject(new Error(e.data.message));
      else w.resolve({ audio: e.data.audio, rate: e.data.rate });
    };
  }
  const id = ++seq;
  return new Promise((resolve, reject) => {
    waiting.set(id, { resolve, reject, onProgress });
    worker!.postMessage({ id, ...msg });
  });
}

let speaker: Promise<void> | undefined;

/** Kokoro-82M, loaded in the worker and ready to speak. */
export function loadSpeaker(onProgress?: Progress): Promise<void> {
  speaker ??= (async () => {
    await ask({ type: 'load', runtime: await runtimePaths(), modelHost: modelHost() }, progressReporter("Getting Alexa's on-device voice", onProgress));
  })().catch((err) => {
    speaker = undefined;
    throw err;
  });
  return speaker;
}

/** Short pieces for speech: sentences, and long sentences at their commas, so audio starts sooner. */
function pieces(text: string): string[] {
  const sentences = text.replace(/&/g, ' and ').match(/[^.!?]+[.!?]*/g)?.map((x) => x.trim()).filter(Boolean) ?? [];
  return sentences.flatMap((sentence) => {
    if (sentence.length <= 60) return [sentence];
    const parts = sentence.split(/(?<=,)\s+/);
    const out: string[] = [];
    for (const part of parts) {
      if (out.length && out[out.length - 1]!.length + part.length < 45) out[out.length - 1] += ` ${part}`;
      else out.push(part);
    }
    return out;
  });
}

/**
 * Speak text piece by piece: the first piece plays while the next is synthesized in the worker,
 * so the reply starts quickly. `stop()` cuts it off (barge-in).
 */
export function speakNeural(text: string, voice = 'af_heart'): { done: Promise<void>; stop: () => void } {
  const ctx = new AudioContext();
  let stopped = false;
  let current: AudioBufferSourceNode | undefined;
  const parts = pieces(text);
  const asked = performance.now();
  const done = (async () => {
    await loadSpeaker();
    let next = parts[0] ? ask({ type: 'speak', text: parts[0], voice }) : undefined;
    for (let i = 0; i < parts.length && !stopped; i++) {
      const { audio, rate } = await next!;
      next = parts[i + 1] ? ask({ type: 'speak', text: parts[i + 1], voice }) : undefined;
      if (stopped || !audio || !rate) break;
      const buffer = ctx.createBuffer(1, audio.length, rate);
      buffer.copyToChannel(audio as Float32Array<ArrayBuffer>, 0);
      current = ctx.createBufferSource();
      current.buffer = buffer;
      current.connect(ctx.destination);
      if (i === 0) console.debug(`[voice] speaking after ${Math.round(performance.now() - asked)} ms (${parts.length} pieces)`);
      await new Promise<void>((resolve) => {
        current!.onended = () => resolve();
        current!.start();
      });
    }
    await ctx.close();
  })();
  return {
    done,
    stop: () => {
      stopped = true;
      try {
        current?.stop();
      } catch {
        /* already ended */
      }
    },
  };
}
